import {
  CheckAchievementsRequest,
  CheckAchievementsResponse,
  GetAchievementsRequest,
  GetAchievementsResponse,
  AwardAchievementRequest,
  AwardAchievementResponse,
  AchievementTrigger,
  UnlockedAchievement,
  AchievementTemplate,
  AchievementProgress,
  ACHIEVEMENT_TEMPLATES,
  ACHIEVEMENT_DISPLAY
} from '../../types/achievement';
import { Achievement } from '../../types/database';

export class AchievementAPI {
  /**
   * Check for new achievements that should be unlocked
   */
  static async checkAchievements(request: CheckAchievementsRequest): Promise<CheckAchievementsResponse> {
    try {
      const response = await fetch('/.netlify/functions/checkAchievements', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: CheckAchievementsResponse = await response.json();
      return result;
    } catch (error) {
      console.error('Error checking achievements:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to check achievements'
      };
    }
  }

  /**
   * Get all achievements for a couple (unlocked and locked)
   */
  static async getAchievements(request: GetAchievementsRequest): Promise<GetAchievementsResponse> {
    try {
      const response = await fetch('/.netlify/functions/getAchievements', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: GetAchievementsResponse = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching achievements:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch achievements'
      };
    }
  }

  /**
   * Award achievement to a couple (internal API function)
   */
  static async awardAchievement(request: AwardAchievementRequest): Promise<AwardAchievementResponse> {
    try {
      // This would typically be called by backend functions, not frontend
      // For now, we'll implement it as a placeholder
      const response = await fetch('/api/achievements/award', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: AwardAchievementResponse = await response.json();
      return result;
    } catch (error) {
      console.error('Error awarding achievement:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to award achievement'
      };
    }
  }
}

export class AchievementHelpers {
  /**
   * Format achievement for display
   */
  static formatAchievementForDisplay(
    achievement: Achievement,
    template?: AchievementTemplate
  ): UnlockedAchievement {
    const achievementTemplate = template || ACHIEVEMENT_TEMPLATES.find(t => t.name === achievement.name);
    
    if (!achievementTemplate) {
      // Fallback for achievements without templates
      return {
        ...achievement,
        template: {
          id: achievement.id,
          name: achievement.name,
          description: achievement.description,
          icon: achievement.icon,
          points_reward: achievement.points_awarded,
          category: 'special' as any,
          rarity: 'common' as any,
          criteria: { type: 'special_event' as any, target_value: 1 },
          unlock_conditions: {},
          is_hidden: false,
          is_secret: false
        },
        time_ago: this.formatTimeAgo(achievement.unlocked_at),
        is_new: this.isRecentlyUnlocked(achievement.unlocked_at)
      };
    }

    return {
      ...achievement,
      template: achievementTemplate,
      time_ago: this.formatTimeAgo(achievement.unlocked_at),
      is_new: this.isRecentlyUnlocked(achievement.unlocked_at)
    };
  }

  /**
   * Format time ago string
   */
  static formatTimeAgo(dateString: string): string {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
    } else {
      const months = Math.floor(diffInDays / 30);
      return `${months} month${months === 1 ? '' : 's'} ago`;
    }
  }

  /**
   * Check if achievement was recently unlocked (within 24 hours)
   */
  static isRecentlyUnlocked(dateString: string): boolean {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    return diffInHours <= 24;
  }

  /**
   * Get rarity color for achievement
   */
  static getRarityColor(template: AchievementTemplate): string {
    return ACHIEVEMENT_DISPLAY.RARITY_COLORS[template.rarity];
  }

  /**
   * Get category icon for achievement
   */
  static getCategoryIcon(template: AchievementTemplate): string {
    return ACHIEVEMENT_DISPLAY.CATEGORY_ICONS[template.category];
  }

  /**
   * Sort achievements by rarity and unlock date
   */
  static sortAchievements(achievements: UnlockedAchievement[]): UnlockedAchievement[] {
    const rarityOrder = ['legendary', 'epic', 'rare', 'uncommon', 'common'];
    
    return achievements.sort((a, b) => {
      // First sort by rarity
      const rarityDiff = rarityOrder.indexOf(a.template.rarity) - rarityOrder.indexOf(b.template.rarity);
      if (rarityDiff !== 0) return rarityDiff;
      
      // Then by unlock date (newest first)
      return new Date(b.unlocked_at).getTime() - new Date(a.unlocked_at).getTime();
    });
  }

  /**
   * Filter achievements by category
   */
  static filterByCategory(
    achievements: UnlockedAchievement[],
    category: string | null
  ): UnlockedAchievement[] {
    if (!category || category === 'all') {
      return achievements;
    }
    
    return achievements.filter(achievement => achievement.template.category === category);
  }

  /**
   * Filter achievements by rarity
   */
  static filterByRarity(
    achievements: UnlockedAchievement[],
    rarity: string | null
  ): UnlockedAchievement[] {
    if (!rarity || rarity === 'all') {
      return achievements;
    }
    
    return achievements.filter(achievement => achievement.template.rarity === rarity);
  }

  /**
   * Get progress bar color based on progress
   */
  static getProgressColor(progress: AchievementProgress): string {
    if (progress.is_unlocked) {
      return 'bg-green-500';
    } else if (progress.is_close_to_unlock) {
      return 'bg-yellow-500';
    } else if (progress.progress_percentage > 50) {
      return 'bg-blue-500';
    } else {
      return 'bg-gray-400';
    }
  }

  /**
   * Format progress text
   */
  static formatProgressText(progress: AchievementProgress, template: AchievementTemplate): string {
    if (progress.is_unlocked) {
      return 'Unlocked!';
    }

    const { current_value, target_value } = progress;

    switch (template.criteria.type) {
      case 'expense_count':
        return `${current_value}/${target_value} expenses`;
      
      case 'expense_amount':
        return `$${current_value.toFixed(2)}/$${target_value.toFixed(2)}`;
      
      case 'challenge_completion':
        return `${current_value}/${target_value} challenges`;
      
      case 'category_diversity':
        return `${current_value}/${target_value} categories`;
      
      case 'streak_achievement':
        return `${current_value}/${target_value} days`;
      
      default:
        return `${current_value}/${target_value}`;
    }
  }

  /**
   * Get achievement notification message
   */
  static getNotificationMessage(achievement: Achievement, template: AchievementTemplate): string {
    return `🎉 Achievement Unlocked: "${template.name}" (+${achievement.points_awarded} points)`;
  }

  /**
   * Check if achievements should trigger notifications
   */
  static shouldNotify(achievements: Achievement[]): boolean {
    return achievements.length > 0;
  }

  /**
   * Group achievements by category for display
   */
  static groupByCategory(achievements: UnlockedAchievement[]): Record<string, UnlockedAchievement[]> {
    return achievements.reduce((groups, achievement) => {
      const category = achievement.template.category;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(achievement);
      return groups;
    }, {} as Record<string, UnlockedAchievement[]>);
  }

  /**
   * Get template for secret achievement display
   */
  static getSecretAchievementTemplate(template: AchievementTemplate): AchievementTemplate {
    if (!template.is_secret) return template;

    return {
      ...template,
      name: ACHIEVEMENT_DISPLAY.SECRET_ACHIEVEMENT_NAME,
      description: ACHIEVEMENT_DISPLAY.SECRET_ACHIEVEMENT_DESCRIPTION,
      icon: ACHIEVEMENT_DISPLAY.LOCKED_ACHIEVEMENT_ICON
    };
  }
}